import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Row, Col, Button, Spinner, Form, Alert } from "react-bootstrap";
import "../../assets/scss/custom-colors.scss";
import { updateUserPassword } from "../../Common/data/updateUserPassword";

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword = () => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting, setErrors, resetForm, setStatus }: any
  ) => {
    if (values.newPassword !== values.confirmPassword) {
      setStatus({ form: "Passwords do not match." });
      setSubmitting(false);
      return;
    }

    try {
      const response = await updateUserPassword(
        values.currentPassword,
        values.newPassword
      );
      console.log("API Response:", response);
      resetForm();
      setStatus({ form: "" }); // Clear form error status after successful submission
    } catch (error: any) {
      console.error("Error:", error.message);
      if (error.response && error.response.status === 401) {
        setErrors({ currentPassword: "Incorrect current password" });
      } else {
        setErrors({ submit: error.message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      }}
      validationSchema={Yup.object().shape({
        currentPassword: Yup.string().required("Current password is required"),
        newPassword: Yup.string()
          .min(6, "Password must be at least 6 characters")
          .required("New password is required"),
        confirmPassword: Yup.string()
          .required("Confirm password is required")
      })}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, status }) => (
        <Form noValidate onSubmit={(e) => e.preventDefault()}>
          <Row className="mb-3">
            <h3 className="mb-3">Change Password</h3>
            <Col md={4}>
              <Form.Group controlId="currentPassword">
                <Form.Label>Current Password</Form.Label>
                <div className="password-toggle">
                  <Form.Control
                    type={showCurrentPassword ? "text" : "password"}
                    name="currentPassword"
                    placeholder="Enter current password"
                    isInvalid={!!errors.currentPassword && touched.currentPassword}
                    value={values.currentPassword}
                    onChange={handleChange}
                  />
                  <Button
                    className="btn btn-link position-absolute end-0 bottom-0 text-decoration-none text-muted password-addon"
                    onClick={toggleCurrentPasswordVisibility}
                    variant="light"
                  >
                    <i className={showCurrentPassword ? "bi bi-eye-fill" : "bi bi-eye-slash-fill"} />
                  </Button>
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.currentPassword}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="newPassword">
                <Form.Label>New Password</Form.Label>
                <div className="password-toggle">
                  <Form.Control
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    placeholder="Enter new password"
                    isInvalid={!!errors.newPassword && touched.newPassword}
                    value={values.newPassword}
                    onChange={handleChange}
                  />
                  <Button
                    className="btn btn-link position-absolute end-0 bottom-0 text-decoration-none text-muted password-addon"
                    onClick={toggleNewPasswordVisibility}
                    variant="light"
                  >
                    <i className={showNewPassword ? "bi bi-eye-fill" : "bi bi-eye-slash-fill"} />
                  </Button>
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.newPassword}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="confirmPassword">
                <Form.Label>Confirm New Password</Form.Label>
                <div className="password-toggle">
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    isInvalid={!!errors.confirmPassword && touched.confirmPassword}
                    value={values.confirmPassword}
                    onChange={handleChange}
                  />
                  <Button
                    className="btn btn-link position-absolute end-0 bottom-0 text-decoration-none text-muted password-addon"
                    onClick={toggleConfirmPasswordVisibility}
                    variant="light"
                  >
                    <i className={showConfirmPassword ? "bi bi-eye-fill" : "bi bi-eye-slash-fill"} />
                  </Button>
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.confirmPassword}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {status?.form && (
            <Alert variant="danger">{status.form}</Alert>
          )}

          <Row>
            <Col>
              <Button
                className="custom-secondary-btn"
                type="button" // Change the button type to "button"
                disabled={isSubmitting}
                onClick={() => handleSubmit()} // Manually trigger form submission
              >
                {isSubmitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Change Password"
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default ChangePassword;
